
import { useState, useEffect, ChangeEvent } from "react";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Settings, Wrench, Construction } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { SistemaConfig, EquipamentoSolar } from "@/types";
import { Separator } from "@/components/ui/separator";
import { v4 as uuidv4 } from "uuid";
import { ConfigAutoSection } from "@/components/solar/configuracao/ConfigAutoSection";
import { ConfigManualSection } from "@/components/solar/configuracao/ConfigManualSection";

const ConfiguracaoSistemas = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("tradicional");
  const [configMode, setConfigMode] = useState<"auto" | "manual">("auto");
  
  // Estado para a seção de equipamentos manuais
  const [paineis, setPaineis] = useState<EquipamentoSolar[]>([]);
  const [inversores, setInversores] = useState<EquipamentoSolar[]>([]);
  const [categoriasOutros, setCategoriasOutros] = useState<{ categoria: string; itens: string[] }[]>([]);
  
  // Definir marcas padrão
  const marcasPaineisDefault = ["Canadian Solar", "JinkoSolar", "Trina Solar", "JA Solar", "LONGi Solar", "Risen", "BYD", "Outro"];
  const marcasInversoresDefault = ["Growatt", "Fronius", "SMA", "Huawei", "Goodwe", "Sungrow", "Deye", "ABB", "Outro"];
  
  // Estados para marcas
  const [marcasPaineis, setMarcasPaineis] = useState<string[]>(marcasPaineisDefault);
  const [marcasInversores, setMarcasInversores] = useState<string[]>(marcasInversoresDefault);
  
  // Modelos por marca - valores padrão
  const modelosPorMarcaDefault = {
    "Canadian Solar": ["CS3N-395MS", "CS3W-415P", "CS6R-355M", "HiKu6 Mono 540-560W", "BiHiKu6 535-555W"],
    "JinkoSolar": ["Tiger Neo N-type 415W", "Tiger Pro 72HC 530-550W", "Eagle 66TR 390-410W", "Tiger Pro 78HC 590-610W"],
    "Trina Solar": ["Vertex S+ 425-440W", "Vertex 670W", "Tallmax M 450-470W", "Honey M 325-345W"],
    "JA Solar": ["JAM72S30 535-555W", "JAM60S20 380-400W", "JAM72D30 535-555W", "DeepBlue 3.0 Pro 420-440W"],
    "LONGi Solar": ["Hi-MO 6 430-450W", "Hi-MO 5 540-560W", "Hi-MO 4 450-470W", "Hi-MO X 400-420W"],
    "Risen": ["RSM40-8 400-420W", "RSM144-7 530-550W", "RSM132-8 580-600W", "RSM120-8 550-570W"],
    "BYD": ["BYD 330-26-P60", "BYD 350-36-M5", "BYD 400-30-P60", "BYD 430-60-H5"],
    
    "Growatt": ["MIN 3000-6000TL-X", "MOD 3000-15000TL3-X", "MID 15-25KTL3-X", "MAX 50-100KTL3 LV", "SPF 3000-5000ES"],
    "Fronius": ["Primo 3.0-8.2", "Symo 3.0-20.0", "Symo GEN24 Plus 3-10 kW", "Tauro 50-100 kW", "Primo GEN24 Plus 3-6 kW"],
    "SMA": ["Sunny Boy 3.0-6.0", "Sunny Tripower 5-10", "Sunny Tripower CORE1", "Sunny Tripower CORE2", "Sunny Central"],
    "Huawei": ["SUN2000 2-6KTL", "SUN2000 8-20KTL", "SUN2000 30-40KTL", "SUN2000 50-100KTL", "SUN2000 185KTL"],
    "Goodwe": ["GW3000-NS", "GW5000D-NS", "GW8K-ET", "GW25K-DT", "GW50K-MT"],
    "Sungrow": ["SG3.0RT", "SG5.0RT", "SG10RT", "SG30CX", "SG110CX"],
    "Deye": ["SUN-5K-G05", "SUN-10K-G03", "SUN-20K-G04", "SUN-50K-G04", "SUN-80K-G04"],
    "ABB": ["UNO-DM-3.3-TL-PLUS", "UNO-DM-5.0-TL-PLUS", "TRIO-50.0-TL", "PVS-50-TL", "PVS-175-TL"],
  };
  
  // Estados para modelos
  const [modelosPorMarca, setModelosPorMarca] = useState<Record<string, string[]>>(modelosPorMarcaDefault);
  
  const [painelImageTradicional, setPainelImageTradicional] = useState<File | null>(null);
  const [inversorImageTradicional, setInversorImageTradicional] = useState<File | null>(null);
  const [painelImageMLPE, setPainelImageMLPE] = useState<File | null>(null);
  const [inversorImageMLPE, setInversorImageMLPE] = useState<File | null>(null);
  const [painelImageHibrido, setPainelImageHibrido] = useState<File | null>(null);
  const [inversorImageHibrido, setInversorImageHibrido] = useState<File | null>(null);
  const [painelImageOffGrid, setPainelImageOffGrid] = useState<File | null>(null);
  const [inversorImageOffGrid, setInversorImageOffGrid] = useState<File | null>(null);
  
  const [painelImagePreviewTradicional, setPainelImagePreviewTradicional] = useState<string>("");
  const [inversorImagePreviewTradicional, setInversorImagePreviewTradicional] = useState<string>("");
  const [painelImagePreviewMLPE, setPainelImagePreviewMLPE] = useState<string>("");
  const [inversorImagePreviewMLPE, setInversorImagePreviewMLPE] = useState<string>("");
  const [painelImagePreviewHibrido, setPainelImagePreviewHibrido] = useState<string>("");
  const [inversorImagePreviewHibrido, setInversorImagePreviewHibrido] = useState<string>("");
  const [painelImagePreviewOffGrid, setPainelImagePreviewOffGrid] = useState<string>("");
  const [inversorImagePreviewOffGrid, setInversorImagePreviewOffGrid] = useState<string>("");

  // Carregar dados dos equipamentos e outros itens
  useEffect(() => {
    const paineisSalvos = localStorage.getItem('paineisSolares');
    const inversoresSalvos = localStorage.getItem('inversoresSolares');
    const categoriasSalvas = localStorage.getItem('categoriasOutrosEquipamentos');
    const marcasPaineisSalvas = localStorage.getItem('marcasPaineis');
    const marcasInversoresSalvas = localStorage.getItem('marcasInversores');
    const modelosPaineisSalvos = localStorage.getItem('modelosPaineis');
    const modelosInversoresSalvos = localStorage.getItem('modelosInversores');
    
    if (paineisSalvos) {
      setPaineis(JSON.parse(paineisSalvos));
    }
    
    if (inversoresSalvos) {
      setInversores(JSON.parse(inversoresSalvos));
    }
    
    if (categoriasSalvas) {
      setCategoriasOutros(JSON.parse(categoriasSalvas));
    } else {
      // Configuração padrão para categorias
      const categoriasPadrao = [
        { 
          categoria: "Estruturas", 
          itens: ["Trilho 4,2m", "Middle Clamp", "End Clamp", "Gancho Colonial"] 
        },
        { 
          categoria: "Cabos", 
          itens: ["Cabo Solar 6mm", "Cabo Solar 4mm"] 
        },
        { 
          categoria: "Conectores", 
          itens: ["MC4 Macho", "MC4 Fêmea"] 
        },
        { 
          categoria: "Proteção", 
          itens: ["String Box", "DPS CC", "DPS CA"] 
        }
      ];
      setCategoriasOutros(categoriasPadrao);
      localStorage.setItem('categoriasOutrosEquipamentos', JSON.stringify(categoriasPadrao));
    }
    
    // Carregar marcas personalizadas
    if (marcasPaineisSalvas) {
      setMarcasPaineis(JSON.parse(marcasPaineisSalvas));
    } else {
      localStorage.setItem('marcasPaineis', JSON.stringify(marcasPaineisDefault));
    }
    
    if (marcasInversoresSalvas) {
      setMarcasInversores(JSON.parse(marcasInversoresSalvas));
    } else {
      localStorage.setItem('marcasInversores', JSON.stringify(marcasInversoresDefault));
    }
    
    // Carregar modelos personalizados
    const modelosCombinados = { ...modelosPorMarcaDefault };
    
    if (modelosPaineisSalvos) {
      const modelosPaineis = JSON.parse(modelosPaineisSalvos);
      Object.keys(modelosPaineis).forEach(marca => {
        modelosCombinados[marca] = modelosPaineis[marca];
      });
    } else {
      localStorage.setItem('modelosPaineis', JSON.stringify(
        Object.fromEntries(
          Object.entries(modelosPorMarcaDefault).filter(([key]) => 
            marcasPaineisDefault.includes(key)
          )
        )
      ));
    }
    
    if (modelosInversoresSalvos) {
      const modelosInversores = JSON.parse(modelosInversoresSalvos);
      Object.keys(modelosInversores).forEach(marca => {
        modelosCombinados[marca] = modelosInversores[marca];
      });
    } else {
      localStorage.setItem('modelosInversores', JSON.stringify(
        Object.fromEntries(
          Object.entries(modelosPorMarcaDefault).filter(([key]) => 
            marcasInversoresDefault.includes(key)
          )
        )
      ));
    }
    
    setModelosPorMarca(modelosCombinados);
  }, []);

  // Salvar dados no localStorage quando houver alterações
  useEffect(() => {
    if (paineis.length > 0) {
      localStorage.setItem('paineisSolares', JSON.stringify(paineis));
    }
  }, [paineis]);
  
  useEffect(() => {
    if (inversores.length > 0) {
      localStorage.setItem('inversoresSolares', JSON.stringify(inversores));
    }
  }, [inversores]);
  
  useEffect(() => {
    if (categoriasOutros.length > 0) {
      localStorage.setItem('categoriasOutrosEquipamentos', JSON.stringify(categoriasOutros));
    }
  }, [categoriasOutros]);

  const handleFileUpload = (
    e: ChangeEvent<HTMLInputElement>, 
    setFile: (file: File | null) => void, 
    setPreview: (url: string) => void, 
    setFormValue: (value: string) => void
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("O arquivo deve ser uma imagem (jpg, jpeg, png, etc)");
      return;
    }

    setFile(file);
    
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setPreview(result);
      setFormValue(result);
    };
    reader.readAsDataURL(file);
  };

  // Carregar imagens de preview após carregamento inicial dos formulários
  useEffect(() => {
    const carregarImagens = async () => {
      try {
        const tradicional = localStorage.getItem('sistema_tradicional');
        const mlpe = localStorage.getItem('sistema_mlpe');
        const hibrido = localStorage.getItem('sistema_hibrido');
        const offgrid = localStorage.getItem('sistema_offgrid');
        
        if (tradicional) {
          const data = JSON.parse(tradicional);
          setPainelImagePreviewTradicional(data.fotoPadraoPainel || "");
          setInversorImagePreviewTradicional(data.fotoPadraoInversor || "");
        }
        
        if (mlpe) {
          const data = JSON.parse(mlpe);
          setPainelImagePreviewMLPE(data.fotoPadraoPainel || "");
          setInversorImagePreviewMLPE(data.fotoPadraoInversor || "");
        }
        
        if (hibrido) {
          const data = JSON.parse(hibrido);
          setPainelImagePreviewHibrido(data.fotoPadraoPainel || "");
          setInversorImagePreviewHibrido(data.fotoPadraoInversor || "");
        }
        
        if (offgrid) {
          const data = JSON.parse(offgrid);
          setPainelImagePreviewOffGrid(data.fotoPadraoPainel || "");
          setInversorImagePreviewOffGrid(data.fotoPadraoInversor || "");
        }
      } catch (error) {
        console.error("Erro ao carregar imagens:", error);
      }
    };
    
    carregarImagens();
  }, []);

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6 max-w-screen-xl">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-green-700 flex items-center gap-2">
              <Settings className="h-8 w-8" /> Configuração de Sistemas
            </h1>
            <p className="text-gray-600 mt-2">
              Configure os parâmetros padrões para diferentes tipos de sistemas fotovoltaicos.
            </p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate("/solar")} 
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" /> Voltar
          </Button>
        </div>
        
        <div className="mb-6">
          <div className="flex gap-4 mb-4">
            <Button 
              variant={configMode === "auto" ? "default" : "outline"} 
              onClick={() => setConfigMode("auto")}
              className="flex items-center gap-2"
            >
              <Settings className="h-4 w-4" />
              Configurações Automáticas
            </Button>
            <Button 
              variant={configMode === "manual" ? "default" : "outline"} 
              onClick={() => setConfigMode("manual")}
              className="flex items-center gap-2"
            >
              <Wrench className="h-4 w-4" />
              Configurações Manuais
            </Button>
          </div>
          <Separator />
        </div>
        
        {configMode === "auto" ? (
          <ConfigAutoSection 
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            painelImagePreviewTradicional={painelImagePreviewTradicional}
            inversorImagePreviewTradicional={inversorImagePreviewTradicional}
            painelImagePreviewMLPE={painelImagePreviewMLPE}
            inversorImagePreviewMLPE={inversorImagePreviewMLPE}
            painelImagePreviewHibrido={painelImagePreviewHibrido}
            inversorImagePreviewHibrido={inversorImagePreviewHibrido}
            painelImagePreviewOffGrid={painelImagePreviewOffGrid}
            inversorImagePreviewOffGrid={inversorImagePreviewOffGrid}
            setPainelImagePreviewTradicional={setPainelImagePreviewTradicional}
            setInversorImagePreviewTradicional={setInversorImagePreviewTradicional}
            setPainelImagePreviewMLPE={setPainelImagePreviewMLPE}
            setInversorImagePreviewMLPE={setInversorImagePreviewMLPE}
            setPainelImagePreviewHibrido={setPainelImagePreviewHibrido}
            setInversorImagePreviewHibrido={setInversorImagePreviewHibrido}
            setPainelImagePreviewOffGrid={setPainelImagePreviewOffGrid}
            setInversorImagePreviewOffGrid={setInversorImagePreviewOffGrid}
            setPainelImageTradicional={setPainelImageTradicional}
            setInversorImageTradicional={setInversorImageTradicional}
            setPainelImageMLPE={setPainelImageMLPE}
            setInversorImageMLPE={setInversorImageMLPE}
            setPainelImageHibrido={setPainelImageHibrido}
            setInversorImageHibrido={setInversorImageHibrido}
            setPainelImageOffGrid={setPainelImageOffGrid}
            setInversorImageOffGrid={setInversorImageOffGrid}
            handleFileUpload={handleFileUpload}
          />
        ) : (
          <ConfigManualSection 
            paineis={paineis}
            setPaineis={setPaineis}
            inversores={inversores}
            setInversores={setInversores}
            categoriasOutros={categoriasOutros}
            setCategoriasOutros={setCategoriasOutros}
            marcasPaineis={marcasPaineis}
            marcasInversores={marcasInversores}
            modelosPorMarca={modelosPorMarca}
          />
        )}
      </div>
    </Layout>
  );
};

export default ConfiguracaoSistemas;
